import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
import { getUnifiedToken } from "../utils/unifiedAuth";
const API_BASE = import.meta.env.VITE_API_BASE ||
    "";
function normalizeRequestedMode(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase();
    return (normalized === "local" ||
        normalized === "offline" ||
        normalized === "offline_local")
        ? "local"
        : "online";
}
function normalizeEffectiveMode(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase();
    if (normalized === "local") {
        return "local";
    }
    if (normalized === "online") {
        return "online";
    }
    if (normalized === "disconnected") {
        return "disconnected";
    }
    return null;
}
function readLegacyMode() {
    try {
        return normalizeRequestedMode(localStorage.getItem("ava_mode"));
    }
    catch {
        return "online";
    }
}
function persistLegacyMirror(mode) {
    try {
        localStorage.setItem("ava_mode", mode);
    }
    catch { }
}
function resolveErrorMessage(payload, fallback) {
    const value = payload?.detail ||
        payload?.message ||
        payload?.error ||
        fallback;
    return String(value || fallback);
}
function stateFromPayload(payload, previous) {
    const requestedMode = normalizeRequestedMode(payload?.requested_mode ??
        payload?.connection_mode ??
        previous.requestedMode);
    const effectiveMode = normalizeEffectiveMode(payload?.effective_mode ??
        payload?.mode);
    const status = String(payload?.status ??
        payload?.connection_status ??
        (effectiveMode === "local"
            ? "selected_local"
            : effectiveMode === "disconnected"
                ? "disconnected"
                : "online")).trim();
    const reason = String(payload?.reason ??
        payload?.connection_reason ??
        (requestedMode === "local"
            ? "protected_local_engine"
            : "account_preference")).trim();
    const localReady = typeof payload?.local_ready ===
        "boolean"
        ? payload.local_ready
        : payload?.local_brain_health === "ok"
            ? true
            : payload?.local_brain_health ===
                "unavailable"
                ? false
                : previous.localReady;
    const cloudServicesBlocked = typeof payload?.cloud_services_blocked ===
        "boolean"
        ? payload.cloud_services_blocked
        : requestedMode === "local";
    const voiceInputAvailable = typeof payload?.voice_input_available ===
        "boolean"
        ? payload.voice_input_available
        : requestedMode !== "local";
    const voiceOutputMode = String(payload?.voice_output_mode ??
        (requestedMode === "local"
            ? "device_browser_only"
            : "cloud_tts")).trim();
    return {
        ready: true,
        loading: false,
        pending: false,
        confirmed: true,
        requestedMode,
        effectiveMode,
        status,
        reason,
        localReady,
        cloudServicesBlocked,
        voiceInputAvailable,
        voiceOutputMode,
        error: "",
    };
}
const provisionalMode = readLegacyMode();
const initialState = {
    ready: false,
    loading: true,
    pending: false,
    confirmed: false,
    requestedMode: provisionalMode,
    effectiveMode: null,
    status: "checking",
    reason: "awaiting_backend_confirmation",
    localReady: null,
    cloudServicesBlocked: provisionalMode === "local",
    voiceInputAvailable: false,
    voiceOutputMode: provisionalMode === "local"
        ? "device_browser_only"
        : "disabled_until_confirmed",
    error: "",
};
const ConnectionModeContext = createContext(null);
export function ConnectionModeProvider({ children, }) {
    const [state, setState] = useState(initialState);
    const stateRef = useRef(initialState);
    const requestSequenceRef = useRef(0);
    const publishState = useCallback((next, options) => {
        stateRef.current = next;
        setState(next);
        if (next.confirmed) {
            persistLegacyMirror(next.requestedMode);
        }
        try {
            window.dispatchEvent(new CustomEvent("avaaio-connection-mode-confirmed", {
                detail: {
                    ...next,
                    announce: options?.announce === true,
                },
            }));
        }
        catch { }
        return next;
    }, []);
    const authorizedHeaders = useCallback(() => {
        const token = getUnifiedToken();
        return {
            Accept: "application/json",
            ...(token
                ? {
                    Authorization: `Bearer ${token}`,
                }
                : {}),
        };
    }, []);
    const applyPayload = useCallback((payload, options) => {
        const next = stateFromPayload(payload, stateRef.current);
        return publishState(next, options);
    }, [publishState]);
    const refresh = useCallback(async () => {
        const sequence = ++requestSequenceRef.current;
        const loadingState = {
            ...stateRef.current,
            loading: true,
            error: "",
        };
        stateRef.current =
            loadingState;
        setState(loadingState);
        try {
            const response = await fetch(`${API_BASE}/api/v1/auth/connection-mode`, {
                method: "GET",
                credentials: "include",
                headers: authorizedHeaders(),
            });
            const payload = await response
                .json()
                .catch(() => ({}));
            if (!response.ok) {
                throw new Error(resolveErrorMessage(payload, `Connection mode request failed (${response.status})`));
            }
            if (sequence !==
                requestSequenceRef.current) {
                return stateRef.current;
            }
            return applyPayload(payload, {
                announce: false,
            });
        }
        catch (error) {
            if (sequence !==
                requestSequenceRef.current) {
                return stateRef.current;
            }
            if (typeof navigator !== "undefined" &&
                navigator.onLine === false) {
                const offlineState = {
                    ...stateRef.current,
                    ready: true,
                    loading: false,
                    pending: false,
                    confirmed: true,
                    requestedMode: "local",
                    effectiveMode: "local",
                    status: "offline_local",
                    reason: "device_offline",
                    localReady: true,
                    cloudServicesBlocked: true,
                    voiceInputAvailable: false,
                    voiceOutputMode: "device_browser_only",
                    error: "",
                };
                persistLegacyMirror("local");
                return publishState(offlineState, {
                    announce: false,
                });
            }
            const failedState = {
                ...stateRef.current,
                ready: true,
                loading: false,
                pending: false,
                confirmed: false,
                effectiveMode: null,
                status: "auth_or_sync_required",
                reason: "backend_confirmation_unavailable",
                voiceInputAvailable: false,
                voiceOutputMode: stateRef.current
                    .requestedMode === "local"
                    ? "device_browser_only"
                    : "disabled_until_confirmed",
                error: String(error?.message ||
                    "Unable to confirm connection mode."),
            };
            return publishState(failedState, {
                announce: false,
            });
        }
    }, [
        applyPayload,
        authorizedHeaders,
        publishState,
    ]);
    const setMode = useCallback(async (mode) => {
        const requestedMode = normalizeRequestedMode(mode);
        const pendingState = {
            ...stateRef.current,
            pending: true,
            error: "",
        };
        stateRef.current =
            pendingState;
        setState(pendingState);
        try {
            const response = await fetch(`${API_BASE}/api/v1/auth/connection-mode`, {
                method: "PATCH",
                credentials: "include",
                headers: {
                    ...authorizedHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    connection_mode: requestedMode,
                }),
            });
            const payload = await response
                .json()
                .catch(() => ({}));
            if (!response.ok) {
                throw new Error(resolveErrorMessage(payload, `Connection mode update failed (${response.status})`));
            }
            return applyPayload(payload, {
                announce: true,
            });
        }
        catch (error) {
            if (requestedMode === "local" &&
                typeof navigator !== "undefined" &&
                navigator.onLine === false) {
                const offlineState = {
                    ...stateRef.current,
                    ready: true,
                    loading: false,
                    pending: false,
                    confirmed: true,
                    requestedMode: "local",
                    effectiveMode: "local",
                    status: "offline_local",
                    reason: "device_offline",
                    localReady: true,
                    cloudServicesBlocked: true,
                    voiceInputAvailable: false,
                    voiceOutputMode: "device_browser_only",
                    error: "",
                };
                persistLegacyMirror("local");
                return publishState(offlineState, {
                    announce: true,
                });
            }
            const failedState = {
                ...stateRef.current,
                pending: false,
                error: String(error?.message ||
                    "Unable to update connection mode."),
            };
            stateRef.current =
                failedState;
            setState(failedState);
            throw error;
        }
    }, [
        applyPayload,
        authorizedHeaders,
    ]);
    // AVAAIO_CONNECTION_MODE_NESTED_RECONCILE_START
    const reconcileFromChatPayload = useCallback((payload) => {
        if (!payload ||
            typeof payload !== "object") {
            return;
        }
        const root = payload;
        const candidates = [
            root,
            root.metadata,
            root.meta,
            root.connection,
            root.connection_metadata,
            root.data,
            root.result,
        ];
        for (const candidate of candidates) {
            if (!candidate ||
                typeof candidate !==
                    "object") {
                continue;
            }
            const data = candidate;
            const containsContract = "requested_mode" in data ||
                "effective_mode" in data ||
                "connection_status" in data ||
                "connection_reason" in data ||
                "cloud_services_blocked" in
                    data ||
                "local_brain_health" in data ||
                "voice_input_available" in
                    data ||
                "voice_output_mode" in data;
            if (!containsContract) {
                continue;
            }
            applyPayload(data, {
                announce: false,
            });
            return;
        }
    }, [applyPayload]);
    // AVAAIO_CONNECTION_MODE_NESTED_RECONCILE_END
    useEffect(() => {
        void refresh();
    }, [refresh]);
    return (_jsx(ConnectionModeContext.Provider, { value: {
            state,
            refresh,
            setMode,
            reconcileFromChatPayload,
        }, children: children }));
}
export function useConnectionMode() {
    const value = useContext(ConnectionModeContext);
    if (!value) {
        throw new Error("useConnectionMode must be used inside ConnectionModeProvider");
    }
    return value;
}
